import React, {memo, useCallback, useEffect, useRef, useState} from "react";
import {ScrollDownIcon, VideoMuteIcon, VideoPauseIcon, VideoPlayIcon, VideoUnmuteIcon} from "./Icons";

interface VideoSectionProps {
    id: string;
    title?: string;
    videoSrc: string;
    showControls?: boolean;
    nextSectionId: string;
}

export const VideoSection: React.FC<VideoSectionProps> = memo(({
                                                                   id,
                                                                   title,
                                                                   videoSrc,
                                                                   showControls = true,
                                                                   nextSectionId = ""
                                                               }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [progress, setProgress] = useState(0);
    const userInteracted = useRef(false);

    const togglePlay = useCallback(() => {
        if (!videoRef.current) return;

        userInteracted.current = true;

        if (videoRef.current.paused) {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        } else {
            videoRef.current.pause();
            setIsPlaying(false);
        }
    }, []);

    const toggleMute = useCallback(() => {
        if (!videoRef.current) return;

        videoRef.current.muted = !videoRef.current.muted;
        setIsMuted(videoRef.current.muted);
    }, []);

    const handleScrollToNext = useCallback(() => {
        const nextSection = document.querySelector(`#${nextSectionId}`);
        if (nextSection) {
            nextSection.scrollIntoView({behavior: 'smooth'});
        }
    }, [nextSectionId]);

    // Progress Tracking
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => {
            if (video.duration) {
                const currentProgress = (video.currentTime / video.duration) * 100;
                setProgress(currentProgress);
            }
        };

        const handleLoadedMetadata = () => {
            video.muted = isMuted;
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, [isMuted]);

    // Intersection Observer for AutoPlay
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries;

            if (userInteracted.current) return;

            if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
                video.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
            } else {
                video.pause();
                setIsPlaying(false);
            }
        };

        const observer = new IntersectionObserver(observerCallback, {
            threshold: [0.5],
        });

        observer.observe(video);
        return () => observer.disconnect();
    }, []);

    return (
        <section id={id} className="snap-section relative w-full bg-dark-mode overflow-hidden">
            <video
                ref={videoRef}
                src={videoSrc}
                loop
                muted={isMuted}
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                aria-label={`Video: ${title}`}
            />

            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-black bg-opacity-30"/>

            {/* Video controls */}
            {showControls && (
                <div
                    className="absolute bottom-4 sm:bottom-6 lg:bottom-10 left-4 sm:left-6 lg:left-10 right-4 sm:right-6 lg:right-10 z-20">
                    <div className="flex items-center gap-3 sm:gap-4">
                        {/* Play/Pause Button */}
                        <button
                            onClick={togglePlay}
                            className="text-ivory flex-shrink-0 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200"
                            aria-label={isPlaying ? 'Pause video' : 'Play video'}
                        >
                            {isPlaying ? (
                                <VideoPauseIcon className="w-6 h-6 sm:w-8 sm:h-8"/>
                            ) : (
                                <VideoPlayIcon className="w-6 h-6 sm:w-8 sm:h-8"/>
                            )}
                        </button>

                        {/* Mute/Unmute Button */}
                        <button
                            onClick={toggleMute}
                            className="text-ivory flex-shrink-0 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200"
                            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                        >
                            {isMuted ? (
                                <VideoMuteIcon className="w-6 h-6 sm:w-8 sm:h-8"/>
                            ) : (
                                <VideoUnmuteIcon className="w-6 h-6 sm:w-8 sm:h-8"/>
                            )}
                        </button>

                        {/* Progress Bar */}
                        <div className="flex-1 bg-white bg-opacity-25 h-1 rounded-full overflow-hidden">
                            <div
                                className="bg-ivory h-full transition-all duration-100"
                                style={{width: `${progress}%`}}
                            />
                        </div>

                        {/* Scroll Down Button */}
                        <div className="hidden sm:block flex-shrink-0">
                            <button
                                onClick={handleScrollToNext}
                                className="flex flex-col items-center space-y-2 cursor-pointer p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200"
                                aria-label="Scroll to next section"
                            >
                                <ScrollDownIcon className="w-5 h-5 sm:w-6 sm:h-6"/>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Title Banner (Only for no controls) */}
            {!showControls && (
                <div
                    className="absolute flex items-center justify-center bg-royal-pine bottom-0 left-0 right-0 z-20">
                    <div className="py-2 sm:py-4 lg:py-6 px-4 sm:px-6">
                        <h1 className="heading-font text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-ivory text-center leading-tight font-light">
                            {title}
                        </h1>
                    </div>
                </div>
            )}
        </section>
    );
});

VideoSection.displayName = "VideoSection";